import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FM111 } from '../../../services/m201Api';
import { AcceptanceFieldHint, pwm201f1AcceptanceManifest } from '../../../features/acceptance';

interface Tab2Props {
  details: Partial<FM111>[];
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onDetailChange: (idx: number, key: keyof FM111, value: FM111[keyof FM111]) => void;
  selectedRow: number;
  onSelectRow: (idx: number) => void;
  totalCount: number;
  documentKey?: string;
}

const Tab2Details: React.FC<Tab2Props> = ({
  details, isEditing, onAdd, onDelete, onDetailChange,
  selectedRow, onSelectRow, totalCount, documentKey,
}) => {
  return (
    <div className="m201-tab2" style={{ marginTop: 12 }}>
      {/* ── Grid ── */}
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table">
          <thead>
            <tr>
              <th style={{ width: 36 }} data-contract-field="fm111_02">
                項次
                <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_01" documentKey={documentKey} />
                <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_02" documentKey={documentKey} />
              </th>
              <th style={{ width: 166 }} data-contract-field="fm111_11">
                產品代號
                <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_11" documentKey={documentKey} />
                <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_12" documentKey={documentKey} />
                <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_13" documentKey={documentKey} />
                <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_14" documentKey={documentKey} />
                <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_15" documentKey={documentKey} />
              </th>
              <th style={{ width: 140 }} data-contract-field="fm111_16">批號<AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_16" documentKey={documentKey} /></th>
              <th style={{ width: 42 }} data-contract-field="fm111_17">單位<AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_17" documentKey={documentKey} /></th>
              <th style={{ width: 79, textAlign: 'right' }} data-contract-field="fm111_18">盤盈虧量<AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_18" documentKey={documentKey} /></th>
              <th style={{ width: 70 }} data-contract-field="fm111_19">異動原因<AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_19" documentKey={documentKey} /></th>
              <th style={{ width: 42, textAlign: 'center' }} data-contract-field="fm111_22">異常<AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_22" documentKey={documentKey} /></th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                  尚無明細資料
                </td>
              </tr>
            )}
            {details.map((d, idx) => (
              <tr
                key={idx}
                onClick={() => onSelectRow(idx)}
                style={{
                  cursor: 'pointer',
                  background: idx === selectedRow ? '#e6f7ff' : undefined,
                }}
              >
                <td>{d.fm111_02 ?? idx + 1}</td>
                <td>
                  <input
                    type="text" maxLength={20}
                    disabled={!isEditing}
                    value={d.fm111_11 || ''}
                    onChange={e => onDetailChange(idx, 'fm111_11', e.target.value)}
                    style={{ width: '100%' }} data-contract-field="fm111_11"
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={18}
                    disabled={!isEditing}
                    value={d.fm111_16 || ''}
                    onChange={e => onDetailChange(idx, 'fm111_16', e.target.value)}
                    placeholder="XXXXXX-X-X-XX-XX"
                    style={{ width: '100%' }} data-contract-field="fm111_16"
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={4}
                    disabled={!isEditing}
                    value={d.fm111_17 || ''}
                    onChange={e => onDetailChange(idx, 'fm111_17', e.target.value)}
                    style={{ width: '100%' }} data-contract-field="fm111_17"
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.01"
                    disabled={!isEditing}
                    value={d.fm111_18 ?? 0}
                    onChange={e => onDetailChange(idx, 'fm111_18', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', textAlign: 'right' }} data-contract-field="fm111_18"
                  />
                </td>
                <td>
                  <select
                    disabled={!isEditing}
                    value={d.fm111_19 || ''}
                    onChange={e => onDetailChange(idx, 'fm111_19', e.target.value)}
                    style={{ width: '100%' }} data-contract-field="fm111_19"
                  >
                    <option value=""></option>
                    <option value="1">1:檢算誤差</option>
                    <option value="2">2:失竊</option>
                    <option value="3">3:短交運或多領或溢入布</option>
                    <option value="4">4:超交運或少領或短入布</option>
                    <option value="5">5:報廢</option>
                    <option value="6">6:縮率調整</option>
                  </select>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    disabled={!isEditing}
                    checked={!!d.fm111_22}
                    onChange={e => onDetailChange(idx, 'fm111_22', e.target.checked as unknown as FM111[keyof FM111])} data-contract-field="fm111_22"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Below-grid strip: 總項數 + 明細新增 + 明細刪除 ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>總項數</span>
        <input
          type="text"
          readOnly
          value={totalCount}
          style={{ width: 45, textAlign: 'center', border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 4px', background: '#f5f5f5', fontSize: 13 }}
        />
        {isEditing && (
          <>
            <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>明細新增</Button>
            <Button
              danger size="small" icon={<DeleteOutlined />}
              disabled={selectedRow < 0 || details.length === 0}
              onClick={() => { if (selectedRow >= 0) onDelete(selectedRow); }}
            >明細刪除</Button>
          </>
        )}
      </div>

      {/* ── Legend: exact FoxPro Label4 captionRaw ── */}
      <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(0,0,0,0.45)', lineHeight: 1.6 }}>
        異動原因：1:檢算誤差,2:失竊,3:短交運或多領或溢入布,4:超交運或少領或短入布,5:報廢,6:縮率調整
      </div>

      {/* ── 異動說明: (always visible, bound to selected row fm111_211) ── */}
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)', whiteSpace: 'nowrap' }}>{'異動說明: '}</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm111_211" documentKey={documentKey} />
        <input
          type="text"
          maxLength={60}
          disabled={!isEditing || details.length === 0 || selectedRow < 0}
          value={(selectedRow >= 0 && details[selectedRow]?.fm111_211) || ''}
          onChange={e => { if (selectedRow >= 0) onDetailChange(selectedRow, 'fm111_211', e.target.value); }}
          style={{
            width: 422,
            padding: '4px 8px',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            fontSize: 13,
            background: (!isEditing || details.length === 0 || selectedRow < 0) ? '#f5f5f5' : undefined,
            color: (!isEditing || details.length === 0 || selectedRow < 0) ? 'rgba(0,0,0,0.65)' : undefined,
          }} data-contract-field="fm111_211"
        />
      </div>
    </div>
  );
};

export default Tab2Details;
